import { useCallback, useEffect, useRef, useState } from 'react'
import { GripHorizontal } from 'lucide-react'

import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import JanelaDestacada, {
  BotaoDestacar,
} from '@core/layout/janela-destacada/JanelaDestacada'
import PlanosDeCortePanel from '@viewer/section-box/PlanosDeCortePanel'

/** A janela flutuante que segura os planos de corte.
 *
 *  Mesmo padrão das outras: arrasta-se pela barra de título e fica
 *  onde a pessoa a puser. O conteúdo vive em
 *  `@viewer/section-box/PlanosDeCortePanel`, que não sabe nada de
 *  janelas. */
export default function FloatingPlanosCortePanel() {
  const open = useAppStore((s) => s.planosCortePanelOpen)
  const setOpen = useAppStore((s) => s.setPlanosCortePanelOpen)
  const { z, trazer } = useJanelaAberta('planos-de-corte', open)
  const [position, setPosition] = useState({ x: 300, y: 220 })
  // Destacado numa janela do browser: o conteúdo é o mesmo e mexe nos
  // cortes do 3D da janela principal.
  const [destacada, setDestacada] = useState(false)
  const voltar = useCallback(() => setDestacada(false), [])
  const fechar = useCallback(() => {
    setDestacada(false)
    setOpen(false)
  }, [setOpen])
  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { ...position },
      }
      try {
        ;(e.target as Element).setPointerCapture?.(e.pointerId)
      } catch {
        // Um alvo que já saiu do DOM atira aqui; arrastar continua a
        // funcionar pelos ouvintes da janela.
      }
    },
    [position],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      setPosition({
        x: d.posStart.x + (e.clientX - d.pointerStart.x),
        y: d.posStart.y + (e.clientY - d.pointerStart.y),
      })
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  if (!open) return null

  if (destacada) {
    return (
      <JanelaDestacada
        aberta
        nome="vn2r-planos-corte"
        titulo="Planos de corte"
        features="width=420,height=640,resizable=yes,scrollbars=yes"
        oQue="os Planos de corte"
        aoVoltar={voltar}
        aoFechar={fechar}
      >
        <div className="p-3 text-xs">
          <PlanosDeCortePanel />
        </div>
      </JanelaDestacada>
    )
  }

  return (
    <div
      onPointerDownCapture={trazer}
      className="fixed flex max-h-[70vh] w-[360px] flex-col rounded border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{ zIndex: z, left: position.x, top: position.y }}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex cursor-move items-center justify-center border-b border-[var(--color-border)] py-1 text-[var(--color-text-dim)]"
      >
        <GripHorizontal className="h-3 w-3" aria-hidden />
      </div>
      <div className="flex items-center gap-2 px-3 pt-1">
        <span className="text-xs font-medium">Planos de corte</span>
        <BotaoDestacar
          onClick={() => setDestacada(true)}
          rotulo="Destacar os planos de corte para uma janela do browser"
          className="ml-auto rounded px-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Fechar"
          className="rounded px-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          ✕
        </button>
      </div>
      <div className="overflow-y-auto px-3 pb-3">
        <PlanosDeCortePanel />
      </div>
    </div>
  )
}
